import React, { useEffect, useState, useCallback } from 'react';
import { axiosAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Disc3, ExternalLink } from 'lucide-react';
import logger from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AlbumsTab — admin UI for managing Shopify-backed albums.
 *
 * The Shopify shop domain + storefront token live ONCE in Settings (so the
 * admin only enters them a single time). Each album just needs a Shopify
 * collection (or product) id + a cover + a description.
 */

const empty = {
  title: '',
  artist: 'IAMHETHATIS',
  description: '',
  cover_url: '',
  price: '',
  credits: '',
  extra_images: [],
  track_files: [],
  shopify_component_type: 'collection',
  shopify_component_id: '',
  full_album_shopify_product_id: '',
  shopify_shop_domain: '',
  shopify_storefront_token: '',
  release_date: '',
  sort_order: 0,
  published: true,
};

// Reusable file uploader — uploads to R2 via the admin endpoint and reports
// the public URL back. Used for cover, extra images, and track audio.
// On failure, throws an Error whose .message is the server's detail (so
// callers can surface the actual reason to the admin, not a generic toast).
const useFileUploader = () => {
  const upload = async (file, endpoint, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}${endpoint}`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          // Let the browser set multipart/form-data with the correct boundary.
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 5 * 60 * 1000, // 5 min — large audio files on cellular
        onUploadProgress: onProgress
          ? (e) => {
              if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
            }
          : undefined,
      });
      return res.data.url || res.data.public_url || res.data.audio_url || res.data.cover_url;
    } catch (e) {
      const serverDetail = e && e.response && e.response.data && e.response.data.detail;
      const status = e && e.response && e.response.status;
      const msg = serverDetail
        ? `${serverDetail} (HTTP ${status})`
        : (e && e.message) || 'Network error';
      const err = new Error(msg);
      err.status = status;
      throw err;
    }
  };
  return { upload };
};

const AlbumsTab = () => {
  const [albums, setAlbums] = useState([]);
  const [settings, setSettings] = useState({ shopify_shop_domain: '', shopify_storefront_token: '' });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [credsForm, setCredsForm] = useState({ shopify_shop_domain: '', shopify_storefront_token: '' });
  const [toast, setToast] = useState(null);
  const [uploadingField, setUploadingField] = useState(null); // 'cover' | 'extras' | 'track' | null
  const { upload } = useFileUploader();

  const fetchAll = useCallback(async () => {
    try {
      const [albumsRes, settingsRes] = await Promise.all([
        axiosAuth.get('/api/admin/albums'),
        axiosAuth.get('/api/admin/settings'),
      ]);
      setAlbums(albumsRes.data || []);
      setSettings(settingsRes.data || {});
      setCredsForm({
        shopify_shop_domain: (settingsRes.data && settingsRes.data.shopify_shop_domain) || '',
        shopify_storefront_token: (settingsRes.data && settingsRes.data.shopify_storefront_token) || '',
      });
    } catch (e) {
      logger.error('Failed to load albums', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setShowModal(true);
  };

  const openEdit = (album) => {
    setEditing(album);
    setForm({
      ...empty,
      ...album,
      price: album.price != null ? String(album.price) : '',
      extra_images: album.extra_images || [],
      track_files: album.track_files || [],
      // Don't show overridden creds if they equal the global ones (so the form
      // stays compact and the admin only sees them when they really differ).
      shopify_shop_domain:
        album.shopify_shop_domain && album.shopify_shop_domain !== settings.shopify_shop_domain
          ? album.shopify_shop_domain
          : '',
      shopify_storefront_token:
        album.shopify_storefront_token && album.shopify_storefront_token !== settings.shopify_storefront_token
          ? album.shopify_storefront_token
          : '',
    });
    setShowModal(true);
  };

  // Pre-checks that mirror the backend's audio validation, so the admin
  // gets instant feedback BEFORE waiting on a multi-MB upload.
  const MAX_AUDIO_BYTES = 200 * 1024 * 1024; // matches MAX_AUDIO_SIZE on backend
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;  // matches MAX_IMAGE_SIZE on backend
  const AUDIO_EXT_RE = /\.(mp3|m4a|wav|aac|flac|ogg|oga)$/i;
  const IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif|heic|heif)$/i;

  const [uploadPct, setUploadPct] = useState(0);

  // Upload handlers — use existing admin R2 endpoints.
  const onCoverFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      showToast(`Cover too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Max 10MB.`, 'error');
      return;
    }
    if (!IMAGE_EXT_RE.test(file.name || '')) {
      showToast(`Cover format not supported: ${file.name}. Use PNG, JPG, WEBP, or HEIC.`, 'error');
      return;
    }
    setUploadingField('cover');
    setUploadPct(0);
    try {
      const url = await upload(file, '/api/admin/upload/cover', setUploadPct);
      if (url) setForm((f) => ({ ...f, cover_url: url }));
      showToast('Cover uploaded.');
    } catch (e) {
      showToast(`Cover upload failed: ${e.message}`, 'error');
    } finally {
      setUploadingField(null);
      setUploadPct(0);
    }
  };

  const onExtraImageFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    for (const f of files) {
      if (f.size > MAX_IMAGE_BYTES) {
        showToast(`"${f.name}" too large: ${(f.size / 1024 / 1024).toFixed(1)}MB. Max 10MB.`, 'error');
        return;
      }
      if (!IMAGE_EXT_RE.test(f.name || '')) {
        showToast(`"${f.name}" format not supported. Use PNG, JPG, WEBP, or HEIC.`, 'error');
        return;
      }
    }
    setUploadingField('extras');
    setUploadPct(0);
    try {
      const uploaded = [];
      for (const f of files) {
        const url = await upload(f, '/api/admin/upload/cover', setUploadPct);
        if (url) uploaded.push(url);
      }
      setForm((f) => ({ ...f, extra_images: [...(f.extra_images || []), ...uploaded] }));
      showToast(`${uploaded.length} image(s) uploaded.`);
    } catch (e) {
      showToast(`Image upload failed: ${e.message}`, 'error');
    } finally {
      setUploadingField(null);
      setUploadPct(0);
    }
  };

  const removeExtraImage = (idx) => {
    setForm((f) => ({
      ...f,
      extra_images: (f.extra_images || []).filter((_, i) => i !== idx),
    }));
  };

  const onTrackAudioFile = async (file, title) => {
    if (!file) return;
    if (file.size > MAX_AUDIO_BYTES) {
      showToast(
        `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB. Max is 200MB — please compress or trim.`,
        'error'
      );
      return;
    }
    if (!AUDIO_EXT_RE.test(file.name || '')) {
      showToast(
        `"${file.name}" isn't a recognised audio file. Supported: MP3, M4A, WAV, AAC, FLAC, OGG.`,
        'error'
      );
      return;
    }
    setUploadingField('track');
    setUploadPct(0);
    try {
      const url = await upload(file, '/api/admin/upload/audio', setUploadPct);
      if (url) {
        setForm((f) => ({
          ...f,
          track_files: [
            ...(f.track_files || []),
            {
              title: (title && title.trim()) || file.name.replace(/\.[^.]+$/, ''),
              audio_url: url,
              order: (f.track_files || []).length + 1,
            },
          ],
        }));
        showToast(`Track "${file.name}" uploaded.`);
      }
    } catch (e) {
      showToast(`Track upload failed: ${e.message}`, 'error');
    } finally {
      setUploadingField(null);
      setUploadPct(0);
    }
  };

  const removeTrackFile = (idx) => {
    setForm((f) => ({
      ...f,
      track_files: (f.track_files || []).filter((_, i) => i !== idx),
    }));
  };

  const updateTrackTitle = (idx, newTitle) => {
    setForm((f) => ({
      ...f,
      track_files: (f.track_files || []).map((t, i) => (i === idx ? { ...t, title: newTitle } : t)),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast('Title is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price === '' || form.price == null ? null : Number(form.price),
        sort_order: Number(form.sort_order) || 0,
        track_ids: form.track_ids || [],
        extra_images: form.extra_images || [],
        track_files: form.track_files || [],
      };
      if (editing) {
        await axiosAuth.put(`/api/admin/albums/${editing.album_id}`, payload);
        showToast(`"${form.title}" updated.`);
      } else {
        await axiosAuth.post('/api/admin/albums', payload);
        showToast(`"${form.title}" created. Visit /albums to see it live.`);
      }
      setShowModal(false);
      await fetchAll();
    } catch (e) {
      const detail = (e.response && e.response.data && e.response.data.detail) || e.message;
      showToast(`Save failed: ${detail}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (album) => {
    if (!window.confirm(`Delete album "${album.title}"? This won't affect Shopify — only removes the link.`)) return;
    try {
      await axiosAuth.delete(`/api/admin/albums/${album.album_id}`);
      showToast(`"${album.title}" deleted.`);
      await fetchAll();
    } catch (e) {
      showToast('Delete failed.', 'error');
    }
  };

  const handleSaveCreds = async () => {
    setSavingCreds(true);
    try {
      await axiosAuth.put('/api/admin/settings', {
        shopify_shop_domain: credsForm.shopify_shop_domain.trim(),
        shopify_storefront_token: credsForm.shopify_storefront_token.trim(),
      });
      showToast('Shopify credentials saved.');
      await fetchAll();
    } catch (e) {
      showToast('Could not save Shopify credentials.', 'error');
    } finally {
      setSavingCreds(false);
    }
  };

  if (loading) {
    return <div className="text-[#A3A3A3] py-12" data-testid="albums-tab-loading">Loading albums…</div>;
  }

  const credsConfigured = !!(settings.shopify_shop_domain && settings.shopify_storefront_token);

  return (
    <div className="space-y-8" data-testid="albums-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Shopify-backed</p>
          <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl">ALBUMS</h2>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Define full-album products. Visitors can still buy individual singles via the existing flow.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-[#D4AF37] text-black text-sm uppercase tracking-wider font-semibold hover:bg-[#E5C048] flex items-center gap-2"
          data-testid="album-create-button"
          title="Create a new album"
        >
          <Plus size={16} />
          New Album
        </button>
      </div>

      {/* Shopify credentials — auto-expanded when missing (gates all BUY buttons) */}
      <details
        className={`border rounded-xl p-4 ${credsConfigured ? 'bg-white/[0.03] border-white/10' : 'bg-yellow-500/[0.06] border-yellow-500/40'}`}
        open={!credsConfigured}
        data-testid="shopify-creds-details"
      >
        <summary className="cursor-pointer text-xs uppercase tracking-[0.22em] hover:text-white">
          {credsConfigured ? (
            <span className="text-[#A3A3A3]">
              Shopify storefront credentials <span className="text-green-400 ml-2">✓ Connected</span>
            </span>
          ) : (
            <span className="text-yellow-300">
              ⚠ Shopify storefront credentials missing — BUY buttons will not render until set
            </span>
          )}
        </summary>
        <p className="text-xs mt-3 mb-3 text-[#A3A3A3]">
          {credsConfigured
            ? 'Only edit if you need to switch Shopify stores. Already saved and working.'
            : 'Enter your Shopify shop domain and Storefront API access token. Without these, every BUY button on the site falls back to "Coming soon".'}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Shop domain</label>
            <input
              type="text"
              placeholder="your-store.myshopify.com"
              value={credsForm.shopify_shop_domain}
              onChange={(e) => setCredsForm({ ...credsForm, shopify_shop_domain: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white text-sm rounded"
              data-testid="shopify-shop-domain-input"
            />
          </div>
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Storefront access token</label>
            <input
              type="text"
              placeholder="storefront token"
              value={credsForm.shopify_storefront_token}
              onChange={(e) => setCredsForm({ ...credsForm, shopify_storefront_token: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white text-sm rounded font-mono"
              data-testid="shopify-storefront-token-input"
            />
          </div>
        </div>
        <button
          onClick={handleSaveCreds}
          disabled={savingCreds}
          className="mt-3 px-4 py-2 bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-wider hover:bg-[#D4AF37]/10"
          data-testid="save-shopify-creds-button"
        >
          {savingCreds ? 'Saving…' : 'Save'}
        </button>
      </details>

      {/* Albums grid */}
      {albums.length === 0 ? (
        <div className="bg-[#1A1A1A] border border-dashed border-white/10 rounded-2xl p-10 text-center">
          <Disc3 size={32} className="text-[#D4AF37] mx-auto mb-3" />
          <p className="text-[#A3A3A3] mb-2">No albums yet.</p>
          <p className="text-xs text-[#737373]">
            Click <span className="text-[#D4AF37]">New Album</span> to wire your first Shopify album.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {albums.map((album) => (
            <div
              key={album.album_id}
              className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex gap-4"
              data-testid={`admin-album-row-${album.slug}`}
            >
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#0D0D0D] flex-shrink-0">
                {album.cover_url ? (
                  <img src={album.cover_url} alt={album.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Disc3 size={28} className="text-[#D4AF37]/40" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-['Bebas_Neue'] text-xl truncate">{album.title}</h4>
                  {!album.published && (
                    <span className="text-[10px] uppercase tracking-wider bg-yellow-500/10 text-yellow-400 px-1.5 py-0.5 rounded">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#A3A3A3] mt-0.5">{album.artist || '—'}</p>
                <p className="text-xs text-[#737373] mt-1 font-mono truncate">
                  {album.shopify_component_type}: {album.shopify_component_id}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <a
                    href={`/albums/${album.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                  >
                    Preview <ExternalLink size={11} />
                  </a>
                  <button
                    onClick={() => openEdit(album)}
                    className="text-xs text-white hover:text-[#D4AF37] inline-flex items-center gap-1"
                    data-testid={`album-edit-${album.slug}`}
                  >
                    <Edit size={11} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(album)}
                    className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                    data-testid={`album-delete-${album.slug}`}
                  >
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 overscroll-contain">
          <div
            className="bg-[#0D0D0D] border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[92vh] overflow-y-auto"
            data-testid="album-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0D0D0D] z-10">
              <h3 className="font-['Bebas_Neue'] text-2xl">{editing ? 'Edit Album' : 'New Album'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-[#A3A3A3] hover:text-white" data-testid="album-modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                  data-testid="album-title-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Artist</label>
                <input
                  type="text"
                  value={form.artist}
                  onChange={(e) => setForm({ ...form, artist: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                  data-testid="album-artist-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                  data-testid="album-description-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Cover image</label>
                <div className="mt-1 flex flex-col md:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="https://… or upload →"
                    value={form.cover_url}
                    onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                    className="flex-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                    data-testid="album-cover-input"
                  />
                  <label
                    className="px-3 py-2 bg-[#1A1A1A] border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-wider rounded cursor-pointer hover:bg-[#D4AF37]/10 text-center"
                    data-testid="album-cover-upload-label"
                  >
                    {uploadingField === 'cover' ? 'Uploading…' : 'Upload Cover'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onCoverFile(e.target.files && e.target.files[0])}
                      data-testid="album-cover-upload-input"
                    />
                  </label>
                </div>
                {form.cover_url && (
                  <img src={form.cover_url} alt="cover preview" className="mt-2 w-24 h-24 object-cover rounded" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Album price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="9.99"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                    data-testid="album-price-input"
                  />
                  <p className="text-[10px] text-[#737373] mt-1">
                    Display only. Real charge happens on the Shopify product you create for Buy Full Album.
                  </p>
                </div>
                <div>
                  <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Release date</label>
                  <input
                    type="text"
                    placeholder="2026"
                    value={form.release_date || ''}
                    onChange={(e) => setForm({ ...form, release_date: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                    data-testid="album-release-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Album credits / liner notes</label>
                <textarea
                  rows={3}
                  placeholder="Produced by… · Written by… · Mixed by…"
                  value={form.credits || ''}
                  onChange={(e) => setForm({ ...form, credits: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                  data-testid="album-credits-input"
                />
              </div>

              {/* Extra artwork / photos */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Extra Artwork / Photos</p>
                  <label
                    className="px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-wider rounded cursor-pointer hover:bg-[#D4AF37]/10"
                    data-testid="album-extras-upload-label"
                  >
                    {uploadingField === 'extras' ? 'Uploading…' : '+ Upload images'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => onExtraImageFiles(e.target.files)}
                      data-testid="album-extras-upload-input"
                    />
                  </label>
                </div>
                {(form.extra_images || []).length === 0 ? (
                  <p className="text-xs text-[#737373]">No extra images yet.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {form.extra_images.map((url, i) => (
                      <div key={url + i} className="relative group" data-testid={`album-extra-image-${i}`}>
                        <img src={url} alt={`extra ${i + 1}`} className="w-full aspect-square object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeExtraImage(i)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`album-extra-image-remove-${i}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Album tracks (uploaded into our platform) */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Album Tracks (uploaded files)</p>
                  <label
                    className="px-3 py-1 bg-[#1A1A1A] border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-wider rounded cursor-pointer hover:bg-[#D4AF37]/10"
                    data-testid="album-track-upload-label"
                  >
                    {uploadingField === 'track' ? (uploadPct > 0 ? `Uploading ${uploadPct}%` : 'Uploading…') : '+ Add track'}
                    <input
                      type="file"
                      accept="audio/*,.mp3,.m4a,.wav,.aac,.ogg,.flac"
                      className="hidden"
                      onChange={(e) => onTrackAudioFile(e.target.files && e.target.files[0])}
                      data-testid="album-track-upload-input"
                    />
                  </label>
                </div>
                <p className="text-[10px] text-[#737373] mb-2">
                  Compress files smaller before uploading for faster transfers. Singles purchased via Shopify stay where they are — this is for the full-album bundle.
                </p>
                {(form.track_files || []).length === 0 ? (
                  <p className="text-xs text-[#737373]">No tracks uploaded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {form.track_files.map((t, i) => (
                      <div key={t.audio_url + i} className="flex items-center gap-2 bg-black/40 border border-white/10 rounded px-3 py-2" data-testid={`album-track-row-${i}`}>
                        <span className="text-[#D4AF37] font-['Bebas_Neue'] w-6 text-center">{i + 1}</span>
                        <input
                          type="text"
                          value={t.title || ''}
                          onChange={(e) => updateTrackTitle(i, e.target.value)}
                          className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none"
                          data-testid={`album-track-title-${i}`}
                        />
                        <audio src={t.audio_url} controls className="h-8" style={{ maxWidth: '180px' }} />
                        <button
                          type="button"
                          onClick={() => removeTrackFile(i)}
                          className="text-red-400 hover:text-red-300 text-xs"
                          data-testid={`album-track-remove-${i}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-white/10 pt-4">
                <details className="text-sm">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#D4AF37]">
                    Advanced — Optional Shopify full-album product
                  </summary>
                  <div className="mt-3 space-y-3 pt-3 border-t border-white/5">
                    <p className="text-xs text-[#A3A3A3]">
                      Albums are managed entirely on your site — upload covers, tracks, set the price above. Only use this section if you want to ALSO charge for the full album via a separate Shopify product.
                    </p>
                    <div>
                      <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Full-album Shopify product ID</label>
                      <input
                        type="text"
                        placeholder="(optional) Shopify product id"
                        value={form.full_album_shopify_product_id || ''}
                        onChange={(e) => setForm({ ...form, full_album_shopify_product_id: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded font-mono"
                        data-testid="album-full-product-id-input"
                      />
                    </div>
                  </div>
                </details>
              </div>

              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Sort order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="w-32 mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded"
                  data-testid="album-sort-order-input"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  data-testid="album-published-input"
                />
                <span className="text-sm text-white">Published (visible on /albums)</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10 sticky bottom-0 bg-[#0D0D0D]">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-[#A3A3A3] hover:text-white"
                data-testid="album-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-[#D4AF37] text-black text-sm uppercase tracking-wider font-semibold hover:bg-[#E5C048] disabled:opacity-50"
                data-testid="album-modal-save"
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Album'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[200] px-5 py-3 rounded-lg shadow-2xl text-sm ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-black'
          }`}
          data-testid="album-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AlbumsTab;
